import { useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Real-time collaboration room hook.
 * @param {string|null} roomCode - Room to connect to; null = not in a room.
 * @param {string} name - Display name of this participant.
 * @param {(params:object)=>void} onRemoteParams - Called when other participants update params.
 */
export const useCollabRoom = (roomCode, name, onRemoteParams) => {
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [myId, setMyId] = useState(null);
  const [chat, setChat] = useState([]); // {name, text}
  const wsRef = useRef(null);
  const onRemoteRef = useRef(onRemoteParams);
  onRemoteRef.current = onRemoteParams;

  useEffect(() => {
    if (!roomCode) return;
    // Convert https://xxx to wss://xxx/api/collab/ws/CODE
    const wsUrl = API.replace(/^http/, "ws") + `/collab/ws/${roomCode}?name=${encodeURIComponent(name || "Anon")}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error("WebSocket construction failed:", err);
      toast.error("Cannot connect to collaboration room.");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => toast.error("Collaboration room error.");
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "welcome") {
        setMyId(msg.client_id);
        setParticipants(msg.participants || []);
        if (msg.params && Object.keys(msg.params).length) onRemoteRef.current?.(msg.params);
      } else if (msg.type === "join" || msg.type === "leave") {
        setParticipants(msg.participants || []);
        if (msg.type === "join" && msg.name) toast(`${msg.name} joined the room`);
      } else if (msg.type === "params") {
        onRemoteRef.current?.(msg.params);
      } else if (msg.type === "chat") {
        setChat((c) => [...c.slice(-40), { name: msg.name, text: msg.text }]);
      }
    };

    return () => {
      try { ws.close(); }
      catch (err) { console.warn("ws.close during cleanup:", err?.message || err); }
    };
  }, [roomCode, name]);

  const sendParams = useCallback((params) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "params", params }));
    }
  }, []);

  const sendChat = useCallback((text) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && text.trim()) {
      ws.send(JSON.stringify({ type: "chat", text }));
    }
  }, []);

  return { connected, participants, myId, chat, sendParams, sendChat };
};

export default useCollabRoom;
